import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { MenuTop } from "../../../../components/menu/top/menu-top";
import {
    CompanyPayload,
    createSuperAdminCompany,
    deleteSuperAdminCompany,
    getSuperAdminCompaniesDashboard,
    updateSuperAdminCompany
} from "../../../../features/search/service/company-service";
import { getLanguagesSelect } from "../../../../features/search/service/languages-service";
import { Company } from "../../../../shared/types/company";
import { SelectOption } from "../../../../shared/types/select-options";
import "./styles/companies-dashboard.css";

type CompanyFormState = {
    name: string;
    cnpj: string;
    description: string;
    licenses: string;
    isPlanActive: boolean;
    languageId: string;
};

const emptyForm: CompanyFormState = {
    name: "",
    cnpj: "",
    description: "",
    licenses: "0",
    isPlanActive: true,
    languageId: ""
};

export const CompaniesDashboard = () => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [languages, setLanguages] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState<Company | null>(null);
    const [form, setForm] = useState<CompanyFormState>(emptyForm);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState("");

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            setError("");

            const [companiesResponse, languagesResponse] = await Promise.all([
                getSuperAdminCompaniesDashboard(),
                getLanguagesSelect()
            ]);

            setCompanies(companiesResponse.data);
            setLanguages(languagesResponse);
        } catch (err: any) {
            setError(err.message || "Não foi possível carregar as empresas.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const filteredCompanies = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();

        if (!term) {
            return companies;
        }

        return companies.filter((company) => {
            return (
                company.name?.toLowerCase().includes(term) ||
                company.description?.toLowerCase().includes(term) ||
                company.cnpj?.toLowerCase().includes(term)
            );
        });
    }, [companies, searchTerm]);

    const totals = useMemo(() => {
        return companies.reduce(
            (acc, company) => {
                acc.licenses += Number(company.licenses) || 0;
                acc.used += Number(company.testsUsed) || 0;
                acc.active += company.isPlanActive ? 1 : 0;
                return acc;
            },
            { licenses: 0, used: 0, active: 0 }
        );
    }, [companies]);

    const openAddForm = () => {
        setEditingCompany(null);
        setForm(emptyForm);
        setFormOpen(true);
    };

    const openEditForm = (company: Company) => {
        setEditingCompany(company);
        setForm({
            name: company.name || "",
            cnpj: company.cnpj || "",
            description: company.description || "",
            licenses: String(company.licenses ?? 0),
            isPlanActive: Boolean(company.isPlanActive),
            languageId: company.language_id || company.activeLanguage?.id || ""
        });
        setFormOpen(true);
    };

    const closeForm = () => {
        if (saving) return;
        setFormOpen(false);
        setEditingCompany(null);
        setForm(emptyForm);
    };

    const updateForm = (field: keyof CompanyFormState, value: string | boolean) => {
        setForm((current) => ({ ...current, [field]: value }));
    };

    const buildPayload = (): CompanyPayload => ({
        name: form.name.trim(),
        cnpj: form.cnpj.trim() || null,
        description: form.description.trim(),
        licenses: Number(form.licenses),
        isPlanActive: form.isPlanActive,
        languageId: form.languageId || null
    });

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setError("Nome da empresa é obrigatório.");
            return;
        }

        const licenses = Number(form.licenses);
        if (!Number.isInteger(licenses) || licenses < 0) {
            setError("Licenças deve ser um número inteiro maior ou igual a zero.");
            return;
        }

        try {
            setSaving(true);
            setError("");

            if (editingCompany?.id) {
                await updateSuperAdminCompany(editingCompany.id, buildPayload());
                setSuccessMessage("Empresa atualizada com sucesso.");
            } else {
                await createSuperAdminCompany(buildPayload());
                setSuccessMessage("Empresa adicionada com sucesso.");
            }

            closeForm();
            await fetchCompanies();
        } catch (err: any) {
            setError(err.message || "Não foi possível salvar a empresa.");
        } finally {
            setSaving(false);
        }
    };

    const openDeleteDialog = (company: Company) => {
        setCompanyToDelete(company);
        setDeleteConfirmation("");
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        if (deleting) return;
        setCompanyToDelete(null);
        setDeleteConfirmation("");
        setDeleteDialogOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!companyToDelete?.id) return;

        const hasTests = Number(companyToDelete.testsUsed) > 0;
        const confirmationMatches = deleteConfirmation.trim() === companyToDelete.name;

        if (hasTests && !confirmationMatches) {
            setError("Digite o nome exato da empresa para confirmar a remoção.");
            return;
        }

        try {
            setDeleting(true);
            setError("");

            await deleteSuperAdminCompany(companyToDelete.id, hasTests);
            setSuccessMessage(
                hasTests
                    ? "Empresa removida da listagem e testes preservados no histórico."
                    : "Empresa removida com sucesso."
            );
            closeDeleteDialog();
            await fetchCompanies();
        } catch (err: any) {
            if (err.status === 409) {
                setCompanyToDelete({
                    ...companyToDelete,
                    testsUsed: err.testsUsed
                });
                setError(err.message || "Confirmação explícita necessária.");
                return;
            }

            setError(err.message || "Não foi possível remover a empresa.");
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (date?: string) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString("pt-BR");
    };

    const formatTotalLicenses = (company: Company) => {
        return company.isUnlimited ? "Ilimitado" : Number(company.licenses) || 0;
    };

    const formatRemainingLicenses = (company: Company) => {
        return company.isUnlimited ? "Ilimitado" : Number(company.licensesRemaining) || 0;
    };

    const hasTestsToDelete = Number(companyToDelete?.testsUsed) > 0;
    const canConfirmDelete = !hasTestsToDelete || deleteConfirmation.trim() === companyToDelete?.name;

    return (
        <div>
            <MenuTop />
            <div className="companies-dashboard">
                <div className="companies-dashboard-header">
                    <div>
                        <h1>Empresas</h1>
                        <p>{companies.length} empresas cadastradas</p>
                    </div>
                    <Button
                        variant="contained"
                        startIcon={<AddBusinessIcon />}
                        onClick={openAddForm}
                        className="companies-primary-button"
                    >
                        Adicionar Empresa
                    </Button>
                </div>

                <div className="companies-summary">
                    <div className="companies-summary-item">
                        <span>Ativas</span>
                        <strong>{totals.active}</strong>
                    </div>
                    <div className="companies-summary-item">
                        <span>Licenças</span>
                        <strong>{totals.licenses}</strong>
                    </div>
                    <div className="companies-summary-item">
                        <span>Usadas</span>
                        <strong>{totals.used}</strong>
                    </div>
                </div>

                <div className="companies-toolbar">
                    <SearchIcon className="companies-toolbar-icon" />
                    <TextField
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Buscar por empresa, descrição ou CNPJ"
                        size="small"
                        fullWidth
                    />
                </div>

                {error && (
                    <Alert severity="error" onClose={() => setError("")} className="companies-alert">
                        {error}
                    </Alert>
                )}

                {successMessage && (
                    <Alert severity="success" onClose={() => setSuccessMessage("")} className="companies-alert">
                        {successMessage}
                    </Alert>
                )}

                {loading ? (
                    <div className="companies-loading">Carregando empresas...</div>
                ) : (
                    <TableContainer component={Paper} className="companies-table-container">
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Empresa</TableCell>
                                    <TableCell>CNPJ</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Licenças</TableCell>
                                    <TableCell>Usadas</TableCell>
                                    <TableCell>Restantes</TableCell>
                                    <TableCell>Idioma</TableCell>
                                    <TableCell>Criada em</TableCell>
                                    <TableCell align="center">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCompanies.map((company) => (
                                    <TableRow key={company.id} hover>
                                        <TableCell>
                                            <div className="companies-name-cell">
                                                <BusinessIcon />
                                                <div>
                                                    <strong>{company.name}</strong>
                                                    <span>{company.description || "Sem descrição"}</span>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>{company.cnpj || "N/A"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={company.isPlanActive ? "Ativo" : "Inativo"}
                                                color={company.isPlanActive ? "success" : "default"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{formatTotalLicenses(company)}</TableCell>
                                        <TableCell>{Number(company.testsUsed) || 0}</TableCell>
                                        <TableCell>{formatRemainingLicenses(company)}</TableCell>
                                        <TableCell>{company.activeLanguage?.name || "N/A"}</TableCell>
                                        <TableCell>{formatDate(company.created_at || company.createdAt)}</TableCell>
                                        <TableCell align="center">
                                            <IconButton aria-label="Editar empresa" onClick={() => openEditForm(company)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton aria-label="Remover empresa" color="error" onClick={() => openDeleteDialog(company)}>
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {filteredCompanies.length === 0 && (
                            <div className="companies-empty">Nenhuma empresa encontrada</div>
                        )}
                    </TableContainer>
                )}
            </div>

            <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
                <DialogTitle className="companies-dialog-title">
                    {editingCompany ? "Editar Empresa" : "Adicionar Empresa"}
                    <IconButton aria-label="Fechar" onClick={closeForm} disabled={saving}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className="companies-form">
                    <TextField
                        label="Nome"
                        value={form.name}
                        onChange={(event) => updateForm("name", event.target.value)}
                        fullWidth
                        required
                    />
                    <TextField
                        label="CNPJ"
                        value={form.cnpj}
                        onChange={(event) => updateForm("cnpj", event.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Descrição"
                        value={form.description}
                        onChange={(event) => updateForm("description", event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                    />
                    <TextField
                        label="Licenças"
                        value={form.licenses}
                        onChange={(event) => updateForm("licenses", event.target.value)}
                        type="number"
                        fullWidth
                        inputProps={{ min: 0, step: 1 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="company-language-label">Idioma</InputLabel>
                        <Select
                            labelId="company-language-label"
                            label="Idioma"
                            value={form.languageId}
                            onChange={(event) => updateForm("languageId", event.target.value)}
                        >
                            <MenuItem value="">Sem idioma</MenuItem>
                            {languages.map((language) => (
                                <MenuItem key={language.id} value={language.id}>
                                    {language.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <div className="companies-switch-row">
                        <div>
                            <strong>Plano ativo</strong>
                            <span>{form.isPlanActive ? "Ativo" : "Inativo"}</span>
                        </div>
                        <Switch
                            checked={form.isPlanActive}
                            onChange={(event) => updateForm("isPlanActive", event.target.checked)}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeForm} disabled={saving}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={saving}
                        className="companies-primary-button"
                    >
                        {saving ? "Salvando..." : "Salvar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Remover Empresa</DialogTitle>
                <DialogContent className="companies-delete-dialog">
                    {companyToDelete && (
                        <>
                            <Typography>
                                {hasTestsToDelete
                                    ? `A empresa ${companyToDelete.name} possui ${companyToDelete.testsUsed} testes vinculados.`
                                    : `Tem certeza que deseja remover ${companyToDelete.name}?`}
                            </Typography>
                            {hasTestsToDelete && (
                                <>
                                    <Alert severity="warning">
                                        A empresa será removida da listagem, mas os testes serão preservados no histórico.
                                    </Alert>
                                    <TextField
                                        label="Digite o nome da empresa"
                                        value={deleteConfirmation}
                                        onChange={(event) => setDeleteConfirmation(event.target.value)}
                                        fullWidth
                                    />
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleting}>Cancelar</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting || !canConfirmDelete}
                    >
                        {deleting ? "Removendo..." : "Remover"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
